/**
 * Blog Management Page (Admin Only)
 *
 * Provides system administrators with tools to:
 * - Create, edit, and delete blog posts
 * - Manage drafts and published posts
 * - Filter by status and category
 * - Add SEO metadata
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Table, type TableColumn } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Select } from '../../components/ui/Select';
import blogService from '../../services/blog';
import type { BlogPost, CreateBlogPostData, UpdateBlogPostData } from '../../types/blog';

interface BlogPostFormData {
  title: string;
  slug: string;
  language: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  image_url: string;
  read_time: number;
  status: 'draft' | 'published';
  published_at: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  homepage_featured: boolean;
  homepage_sort_order: number | '';
}

const initialFormData: BlogPostFormData = {
  title: '',
  slug: '',
  language: 'en',
  excerpt: '',
  content: '',
  category: '',
  author: 'Axetra Team',
  image_url: '',
  read_time: 5,
  status: 'draft',
  published_at: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  homepage_featured: false,
  homepage_sort_order: '',
};

export const BlogManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [formData, setFormData] = useState<BlogPostFormData>(initialFormData);

  // Fetch blog posts
  const { data: postsData, isLoading } = useQuery({
    queryKey: ['admin-blog-posts', page, statusFilter, categoryFilter],
    queryFn: () =>
      blogService.getAllBlogPosts({
        status: statusFilter === 'all' ? undefined : statusFilter,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        page,
      }),
  });

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['admin-blog-categories'],
    queryFn: () => blogService.getBlogCategories(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateBlogPostData) => blogService.createBlogPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-blog-categories'] });
      queryClient.invalidateQueries({ queryKey: ['featured-blog-posts'] });
      setSuccess('Blog post created successfully');
      setIsCreateModalOpen(false);
      setFormData(initialFormData);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create blog post');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBlogPostData }) =>
      blogService.updateBlogPost(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['featured-blog-posts'] });
      setSuccess('Blog post updated successfully');
      setIsEditModalOpen(false);
      setSelectedPost(null);
      setFormData(initialFormData);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update blog post');
    },
  });

  // Quick-toggle homepage featured mutation
  const featureMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBlogPostData }) =>
      blogService.updateBlogPost(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['featured-blog-posts'] });
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update featured status');
    },
  });

  const handleToggleFeatured = (post: BlogPost) => {
    if (post.homepage_featured) {
      featureMutation.mutate({ id: post.id, data: { homepage_featured: false, homepage_sort_order: null } });
    } else {
      const usedSlots = (postsData?.data ?? [])
        .filter(p => p.homepage_featured && p.id !== post.id)
        .map(p => p.homepage_sort_order);
      const nextSlot = ([1, 2, 3] as const).find(n => !usedSlots.includes(n));
      if (!nextSlot) {
        setError('All 3 homepage slots are taken. Remove another post first.');
        return;
      }
      featureMutation.mutate({ id: post.id, data: { homepage_featured: true, homepage_sort_order: nextSlot } });
    }
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => blogService.deleteBlogPost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-blog-categories'] });
      queryClient.invalidateQueries({ queryKey: ['featured-blog-posts'] });
      setSuccess('Blog post deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedPost(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete blog post');
    },
  });

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Auto-generate slug from title
  const handleTitleChange = (title: string) => {
    setFormData((prev) => ({
      ...prev,
      title,
      slug: title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim(),
    }));
  };

  const handleCreatePost = () => {
    const dataToSubmit: CreateBlogPostData = {
      ...formData,
      slug: formData.slug || undefined,
      author: formData.author || undefined,
      image_url: formData.image_url || undefined,
      read_time: formData.read_time || undefined,
      published_at: formData.published_at || undefined,
      meta_title: formData.meta_title || undefined,
      meta_description: formData.meta_description || undefined,
      meta_keywords: formData.meta_keywords || undefined,
      homepage_sort_order: formData.homepage_sort_order !== '' ? Number(formData.homepage_sort_order) : null,
    };
    createMutation.mutate(dataToSubmit);
  };

  const handleUpdatePost = () => {
    if (!selectedPost) return;

    const dataToSubmit: UpdateBlogPostData = {
      ...formData,
      slug: formData.slug || undefined,
      author: formData.author || undefined,
      image_url: formData.image_url || undefined,
      read_time: formData.read_time || undefined,
      published_at: formData.published_at || undefined,
      meta_title: formData.meta_title || undefined,
      meta_description: formData.meta_description || undefined,
      meta_keywords: formData.meta_keywords || undefined,
      homepage_sort_order: formData.homepage_sort_order !== '' ? Number(formData.homepage_sort_order) : null,
    };
    updateMutation.mutate({ id: selectedPost.id, data: dataToSubmit });
  };

  const handleDeletePost = () => {
    if (!selectedPost) return;
    deleteMutation.mutate(selectedPost.id);
  };

  const handleEditClick = (post: BlogPost) => {
    setSelectedPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      language: post.language || 'en',
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      image_url: post.image_url || '',
      read_time: post.read_time,
      status: post.status,
      published_at: post.published_at || '',
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      homepage_featured: post.homepage_featured || false,
      homepage_sort_order: post.homepage_sort_order ?? '',
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (post: BlogPost) => {
    setSelectedPost(post);
    setIsDeleteModalOpen(true);
  };

  // Filter posts by search term
  const filteredPosts = React.useMemo(() => {
    if (!postsData?.data) return [];
    if (!searchTerm) return postsData.data;

    const term = searchTerm.toLowerCase();
    return postsData.data.filter(
      (post) =>
        post.title.toLowerCase().includes(term) ||
        post.excerpt.toLowerCase().includes(term) ||
        post.category.toLowerCase().includes(term)
    );
  }, [postsData?.data, searchTerm]);

  // Table columns
  const columns: TableColumn<BlogPost>[] = [
    {
      key: 'title',
      header: 'Title',
      render: (_value, post) => (
        <div>
          <div className="font-medium text-gray-900">{post.title}</div>
          <div className="text-sm text-gray-500">/blog/{post.slug}</div>
        </div>
      ),
    },
    {
      key: 'language',
      header: 'Lang',
      render: (_value, post) => (
        <Badge variant="default">{(post.language || 'en').toUpperCase()}</Badge>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (_value, post) => (
        <Badge variant="default">{post.category}</Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_value, post) => (
        <div className="flex flex-col gap-1">
          <Badge variant={post.status === 'published' ? 'success' : 'warning'}>
            {post.status}
          </Badge>
          {post.homepage_featured && (
            <Badge variant="info">★ Homepage #{post.homepage_sort_order}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'published_at',
      header: 'Published',
      render: (_value, post) =>
        post.published_at
          ? new Date(post.published_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      key: 'read_time',
      header: 'Read Time',
      render: (_value, post) => `${post.read_time} min`,
    },
    {
      key: 'homepage_featured',
      header: 'Hero Page',
      render: (_value, post) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleToggleFeatured(post); }}
          disabled={featureMutation.isPending}
          title={post.homepage_featured ? `Slot #${post.homepage_sort_order} — click to remove` : 'Add to hero page'}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            post.homepage_featured
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <span>{post.homepage_featured ? '★' : '☆'}</span>
          <span>{post.homepage_featured ? `Slot ${post.homepage_sort_order}` : 'Add'}</span>
        </button>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
          <p className="text-gray-600 mt-1">Create and manage blog posts</p>
        </div>
        <Button
          onClick={() => {
            setFormData(initialFormData);
            setIsCreateModalOpen(true);
          }}
        >
          Create Post
        </Button>
      </div>

      {/* Hero page language info */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
        <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>
          <strong>Hero page &amp; languages:</strong> EN posts define the 3 hero slots for all languages. If a translated version of the same slug exists, it shows instead. To promote a post in a specific language only, mark it as featured — it takes priority over the EN fallback for that language.
        </span>
      </div>

      {/* Alerts */}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Search"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'all' | 'draft' | 'published');
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'published', label: 'Published' },
            ]}
          />
          <Select
            label="Category"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All Categories' },
              ...categories.map((cat) => ({ value: cat, label: cat })),
            ]}
          />
        </div>
      </Card>

      {/* Posts Table */}
      <Card>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="text-gray-600 mt-2">Loading posts...</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={filteredPosts}
              renderActions={(post) => (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => handleEditClick(post)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleDeleteClick(post)}>
                    Delete
                  </Button>
                </div>
              )}
            />

            {postsData && postsData.total && postsData.total > (postsData.per_page || 20) && (
              <div className="mt-4">
                <Pagination
                  currentPage={page}
                  totalPages={postsData.last_page || 1}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isCreateModalOpen || isEditModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setFormData(initialFormData);
        }}
        title={isCreateModalOpen ? 'Create Blog Post' : 'Edit Blog Post'}
        size="xl"
      >
        <div className="space-y-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Basic Information</h3>

            <Input
              label="Title *"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input label="Slug" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} />
              <Select
                label="Language"
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                options={[
                  { value: 'en', label: 'English (EN)' },
                  { value: 'ru', label: 'Russian (RU)' },
                  { value: 'es', label: 'Spanish (ES)' },
                  { value: 'de', label: 'German (DE)' },
                ]}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Category *"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              />

              <Input
                label="Author"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Read Time (minutes)"
                type="number"
                value={formData.read_time}
                onChange={(e) => setFormData({ ...formData, read_time: parseInt(e.target.value) || 5 })}
              />

              <Input
                label="Image URL"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt *</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                rows={10}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Publishing */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-semibold text-gray-900">Publishing</h3>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'draft' | 'published' })}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'published', label: 'Published' },
                ]}
              />

              <Input
                label="Published At"
                type="datetime-local"
                value={formData.published_at}
                onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
              />
            </div>
          </div>

          {/* Homepage Featured */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-semibold text-gray-900">Homepage</h3>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={formData.homepage_featured}
                  onChange={(e) => setFormData({ ...formData, homepage_featured: e.target.checked, homepage_sort_order: e.target.checked ? formData.homepage_sort_order || 1 : '' })}
                />
                <span className="text-sm font-medium text-gray-700">Feature this post on the homepage</span>
              </label>
            </div>

            {formData.homepage_featured && (
              <div className="max-w-xs">
                <Select
                  label="Position (1 = first card)"
                  value={String(formData.homepage_sort_order)}
                  onChange={(e) => setFormData({ ...formData, homepage_sort_order: Number(e.target.value) })}
                  options={[
                    { value: '1', label: 'Position 1' },
                    { value: '2', label: 'Position 2' },
                    { value: '3', label: 'Position 3' },
                  ]}
                />
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-semibold text-gray-900">SEO Metadata</h3>

            <Input
              label="Meta Title"
              value={formData.meta_title}
              onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
              />
            </div>

            <Input
              label="Meta Keywords (comma separated)"
              value={formData.meta_keywords}
              onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => {
                setIsCreateModalOpen(false);
                setIsEditModalOpen(false);
                setFormData(initialFormData);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={isCreateModalOpen ? handleCreatePost : handleUpdatePost}
              disabled={!formData.title || !formData.excerpt || !formData.content || !formData.category}
            >
              {isCreateModalOpen ? 'Create Post' : 'Update Post'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPost(null);
        }}
        onConfirm={handleDeletePost}
        title="Delete Blog Post"
        message={`Are you sure you want to delete "${selectedPost?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default BlogManagementPage;
